import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { socialAPI, socialUtils } from "../../services/api";
import { getUserPlaceholderImage } from "../../utils/imageUtils";

const BuddiesGridTab = ({
  userId,
  currentUser,
  isCurrentUserProfile,
  accentColor = "#8548A8",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [connects, setConnects] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sendingUserId, setSendingUserId] = useState(null);

  // Load a specific page of connects
  const loadPage = useCallback(
    async (pageToLoad, { append } = { append: false }) => {
      if (!userId) return;

      try {
        if (append) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true);
        }
        setError(null);

        const params = {
          user_id: userId,
          page: pageToLoad,
          per_page: 20,
        };
        if (search && search.trim()) {
          params.search = search.trim();
        }

        const response = await socialAPI.getConnects(params);
        const payload = response.data || {};

        const list = Array.isArray(payload.data) ? payload.data : [];
        const metaData = payload.meta || null;

        setMeta(metaData);
        setPage(pageToLoad);

        setConnects((prev) =>
          append ? [...prev, ...list] : list
        );
      } catch (err) {
        console.error("Failed to load buddies:", err);
        setError(t("userprofile.connectsModal.error") || "Unable to load buddies.");
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [userId, search, t]
  );

  // Initial load
  useEffect(() => {
    if (!userId) return;
    setConnects([]);
    setMeta(null);
    loadPage(1, { append: false });
  }, [userId, search, loadPage]);

  const hasMore = meta && meta.page && meta.total_pages ? meta.page < meta.total_pages : false;

  const filterUser = (user) => {
    if (!search || !search.trim()) return true;
    const searchLower = search.trim().toLowerCase();
    const displayName = user.display_name || [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username || "";
    const username = user.username || "";
    return displayName.toLowerCase().includes(searchLower) || username.toLowerCase().includes(searchLower);
  };

  const filteredConnects = connects.filter(filterUser);

  const handleLoadMore = () => {
    if (hasMore && !isLoadingMore) {
      loadPage(page + 1, { append: true });
    }
  };

  const handleItemClick = (buddyUser) => {
    const id = buddyUser.id || buddyUser.user_id;
    if (!id) return;

    // If clicking on self, go to main profile path instead of profile/:id
    const currentUserId = currentUser?.id || currentUser?.user_id;
    if (currentUserId && id.toString() === currentUserId.toString()) {
      navigate("/social/profile");
      return;
    }

    navigate(`/social/profile/${id}`);
  };

  const handleConnectClick = async (e, targetUserId) => {
    e.stopPropagation();
    if (!targetUserId) return;
    try {
      setSendingUserId(targetUserId);
      await socialAPI.sendConnectionRequest(targetUserId);
      // Optimistically mark as pending outgoing
      setConnects((prev) =>
        prev.map((u) => {
          const uid = u.id || u.user_id;
          if (uid && uid.toString() === targetUserId.toString()) {
            return {
              ...u,
              connection_status: "pending_outgoing",
            };
          }
          return u;
        })
      );
    } catch (err) {
      console.error("Failed to send connect request:", err);
    } finally {
      setSendingUserId(null);
    }
  };

  const renderInitial = (user) => {
    const first = user.display_name || [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username || "";
    if (!first) return "?";
    return first.charAt(0).toUpperCase();
  };

  return (
    <div className="userprofile-section-card">
      <div className="userprofile-section-header" style={{ marginBottom: "20px", display: "flex", flexWrap: "wrap", gap: "16px", justifyContent: "space-between", alignItems: "center" }}>
        <h2 className="userprofile-section-title" style={{ margin: 0 }}>
          {isCurrentUserProfile ? (t("userprofile.menu.myBuddies") || "My Buddies") : (t("userprofile.stats.connects") || "Buddies")}
          <span className="userprofile-section-count">({meta?.total || connects.length})</span>
        </h2>
        
        {/* Search Input inline with the title */}
        <div style={{ position: "relative", maxWidth: "300px", width: "100%", flex: "1 1 auto" }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("userprofile.connectsModal.searchPlaceholder") || "Search buddies..."}
            style={{
              width: "100%",
              borderRadius: "99px",
              border: "1px solid #E5E7EB",
              padding: "10px 16px",
              paddingLeft: "36px",
              fontSize: "14px",
              outline: "none",
              backgroundColor: "#F9FAFB",
            }}
          />
          <svg style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#9CA3AF" }} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="2" />
            <line x1="15.5" y1="15.5" x2="20" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
      </div>

      {isLoading && connects.length === 0 ? (
        <div className="up-skel-buddy-grid" aria-hidden="true">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={`up-skel-buddy-${i}`} className="up-skel-buddy-card">
              <div className="up-skel up-skel-buddy-avatar" />
              <div className="up-skel up-skel-line up-skel-w60" />
              <div className="up-skel up-skel-pill" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="userprofile-empty-state" style={{ color: "#DC2626" }}>
          <p>{error}</p>
        </div>
      ) : connects.length === 0 ? (
        <div className="userprofile-empty-state">
          <span style={{ fontSize: '32px' }}>👥</span>
          <p>{t("userprofile.connectsModal.empty") || "No buddies yet."}</p>
        </div>
      ) : filteredConnects.length === 0 ? (
        <div className="userprofile-empty-state">
          <p>{t("userprofile.connectsModal.noResults") || "No buddies found matching your search."}</p>
        </div>
      ) : (
        <div 
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "16px",
          }}
        >
          {filteredConnects.map((user) => {
            const id = user.id || user.user_id;
            const currentUserId = currentUser?.id || currentUser?.user_id;
            const isCurrentUser = currentUserId && id && currentUserId.toString() === id.toString();
            const displayName = user.display_name || [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username || t("userprofile.fallbackName");
            const avatar = user.profile_image_url || user.avatar || getUserPlaceholderImage(user);
            const userTypeColor = socialUtils.getUserTypeColor(user.user_type);
            const status = user.connection_status || "none";
            const isSending = sendingUserId && (sendingUserId.toString() === (id || "").toString());

            return (
              <div
                key={id || displayName}
                onClick={() => handleItemClick(user)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "20px 12px",
                  borderRadius: "16px",
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#FFFFFF",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                  textAlign: "center"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.02)";
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    border: `3px solid ${userTypeColor}`,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #f9fafb, #e5e7eb)",
                    marginBottom: "12px",
                  }}
                >
                  {avatar ? (
                    <img
                      src={avatar}
                      alt={displayName}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <span style={{ fontSize: "28px", fontWeight: 600, color: userTypeColor }}>
                      {renderInitial(user)}
                    </span>
                  )}
                </div>
                
                <div style={{ 
                  fontSize: "15px", 
                  fontWeight: 600, 
                  color: "#111827", 
                  marginBottom: "4px",
                  width: "100%",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}>
                  {displayName}
                </div>

                <div style={{ height: "30px", marginTop: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {isCurrentUser ? (
                    <span style={{ padding: "4px 12px", borderRadius: "999px", backgroundColor: "#F3F4F6", color: "#6B7280", fontSize: "12px", fontWeight: 500 }}>
                      {t("userprofile.me") || "Me"}
                    </span>
                  ) : status === "friends" && !isCurrentUserProfile ? (
                    <span style={{ padding: "4px 12px", borderRadius: "999px", backgroundColor: `${accentColor}10`, color: accentColor, fontSize: "12px", fontWeight: 500 }}>
                      {t("userprofile.following")}
                    </span>
                  ) : status === "pending_outgoing" ? (
                    <span style={{ padding: "4px 12px", borderRadius: "999px", backgroundColor: "#F3F4F6", color: "#6B7280", fontSize: "12px", fontWeight: 500 }}>
                      {t("userprofile.pending")}
                    </span>
                  ) : status === "none" ? (
                    <button
                      type="button"
                      onClick={(e) => handleConnectClick(e, id)}
                      disabled={isSending}
                      style={{
                        padding: "6px 16px",
                        borderRadius: "999px",
                        border: "1px solid " + accentColor,
                        backgroundColor: isSending ? "#F3F4F6" : "#FFFFFF",
                        color: accentColor,
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: isSending ? "default" : "pointer",
                        transition: "all 0.2s"
                      }}
                      onMouseEnter={(e) => {
                        if (!isSending) {
                          e.currentTarget.style.backgroundColor = accentColor;
                          e.currentTarget.style.color = "#FFFFFF";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSending) {
                          e.currentTarget.style.backgroundColor = "#FFFFFF";
                          e.currentTarget.style.color = accentColor;
                        }
                      }}
                    >
                      {isSending ? (t("userprofile.buttons.saving") || "Sending...") : (t("userprofile.follow") || "Connect")}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasMore && connects.length > 0 && (
        <div style={{ marginTop: "24px", textAlign: "center" }}>
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="userprofile-load-more-btn"
            style={{
              padding: "10px 24px",
              borderRadius: "999px",
              border: "1px solid " + accentColor,
              background: isLoadingMore ? "#F3F4F6" : "#FFFFFF",
              color: accentColor,
              fontSize: "14px",
              fontWeight: 600,
              cursor: isLoadingMore ? "default" : "pointer",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              if (!isLoadingMore) {
                e.currentTarget.style.backgroundColor = accentColor;
                e.currentTarget.style.color = "#FFFFFF";
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoadingMore) {
                e.currentTarget.style.backgroundColor = "#FFFFFF";
                e.currentTarget.style.color = accentColor;
              }
            }}
          >
            {isLoadingMore ? (t("common.loading") || "Loading...") : (t("userprofile.connectsModal.loadMore") || "Load more buddies")}
          </button>
        </div>
      )}
    </div>
  );
};

export default BuddiesGridTab;
